import React, { useEffect, useRef, useState } from 'react';
import { View, TouchableOpacity, ScrollView, Image, Text, ActivityIndicator } from 'react-native-web';
import { toast } from 'react-hot-toast';
import * as Animatable from 'react-native-animatable';
import FeatherIcon from 'react-native-vector-icons/dist/Feather';
import moment from 'moment';

// Custom Components
import LoadingView from '../../components/component_loading_view';

// Navigation
import * as RootNavigation from '../../navigation/RootNavigation';

// Configs
import GLOBAL from '../../configs/config_global';
import { setGlobalState, STATE_KEYS, useGlobalState } from '../../configs/config_state';

// APIs
import {
    getCategoryList,
    deleteCategory,
    editCategory,
    createCategory,
    getFoodItems,
    uploadCateringMenuSampleFile,
    getCateringMenuSampleFiles,
    deleteCateringMenuSampleFile
} from '../../modules';

// Utils
import { showAlert, showAppError, toTitleCase } from '../../utils/util_helpers';
import { cloneDeep } from 'lodash';
import { isProfilePendingApproval } from '../../utils/util_truck_helpers';
import routeKeys from '../../navigation/RouteKeys';
import { getFileName, getUrlExtension } from '../../utils/util_files';
import { Button } from '../../components';
import ProposalTab from './tab_proposals';
import { useRoute } from '@react-navigation/native';

export function Menu() {
    const { initialTabIndex = 0 } = useRoute().params || {};

    // region Global State
    const [truckData] = useGlobalState(STATE_KEYS.TRUCK_DATA);
    const [squareLocations] = useGlobalState(STATE_KEYS.LOCATION_LIST);
    const [inScreenModalShowing] = useGlobalState(STATE_KEYS.IN_SCREEN_MODAL_SHOWING);
    // endregion

    // region Refs
    const DELAY_VALUE = 150;
    const menuView = useRef(null);
    const cateringSampleView = useRef(null);
    const proposalView = useRef(null);
    const cateringSamplePicker = useRef(null);
    // endregion

    // region Local State
    const [menuData, setMenuData] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [creatingCategoryName, setCreatingCategoryName] = useState('');
    const [isDeletingCategory, setDeletingCategory] = useState(false);
    const [isRenamingCategory, setRenamingCategory] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isLoadingFoodItems, setLoadingFoodItems] = useState(false);
    const [foodListData, setFoodListData] = useState({});
    const [tabIndex, setTabIndex] = useState(0);
    const [sampleMenuFiles, setSampleMenuFiles] = useState([]);
    const [uploadingSampleMenuFile, setUploadingSampleMenuFile] = useState(false);
    const [deletingFileID, setDeletingFileID] = useState(null);
    const [isLoadingCateringSample, setLoadingCateringSample] = useState(false);
    // endregion

    // region Component Mounting
    useEffect(() => {
        goFetchMenu();
        goFetchCateringMenuSamples();
    }, []);
    // endregion

    // region Function - Fetch Menu Data
    const getAllCategoryData = (firstResult) => new Promise((resolve) => {
        if (firstResult.lastPage > 1) {
            let pageArr = [];
            for (let i = 2; i <= firstResult.lastPage; ++i) pageArr = pageArr.concat(i);

            Promise.all(pageArr.map((pageCount) => getCategoryList(truckData.id, pageCount).catch(() => Promise.resolve([]))))
                .then((allResult) => {
                    let finalData = firstResult.data;
                    allResult.forEach((smallResult) => finalData = finalData.concat(smallResult.data));
                    resolve(finalData);
                })
                .catch((error) => {
                    console.log('[FAILED] getAllCategoryData', error);
                    resolve([]);
                });
        }
        else resolve(firstResult.data);
    });

    const goFetchMenu = (isRefreshing = false) => {
        if (isRefreshing && isLoading) return;

        setLoading(true);
        getCategoryList(truckData.id)
            .then((result) => {
                getAllCategoryData(result)
                    .then((unprocessedFoodList) => {
                        let menuData = unprocessedFoodList.map((item) => Object.assign({}, item, { key: `categoryItem_${item.id}` }));
                        if (unprocessedFoodList.length > 1) {
                            if (Number(unprocessedFoodList[0].order) === Number(unprocessedFoodList[1].order)) {
                                menuData = unprocessedFoodList.map((item, index) => Object.assign({}, item, { order: index + 1, key: `categoryItem_${item.id}` }));
                            }
                        }
                        menuData.sort((a, b) => a.order - b.order);

                        // Save data to state
                        setMenuData(menuData);

                        // Save to global state (mainly for ModifierCopyModal)
                        setGlobalState(STATE_KEYS.MENU_DATA, cloneDeep(menuData));

                        // Go to initial tab index
                        setTabIndex(initialTabIndex);
                    });
            })
            .catch((error) => {
                setMenuData([]);
                setGlobalState(STATE_KEYS.MENU_DATA, []);
                setErrorMessage(error.message);
            })
            .finally(() => setLoading(false));
    }
    // endregion

    // region Function - Fetch Food List Data
    const getAllFoodData = (firstResult, categoryData) => {
        return new Promise((resolve) => {
            if (firstResult.lastPage > 1) {
                let pageArr = [];
                for (let i = 2; i <= firstResult.lastPage; ++i) pageArr = pageArr.concat(i);

                Promise.all(pageArr.map((pageCount) => getFoodItems(truckData.id, categoryData.id, pageCount).catch(() => Promise.resolve([]))))
                    .then((allResult) => {
                        let finalData = firstResult.data;
                        allResult.forEach((smallResult) => finalData = finalData.concat(smallResult.data));
                        resolve(finalData);
                    })
                    .catch((error) => {
                        console.log('[FAILED] getAllFoodData', error);
                        resolve([]);
                    });
            }
            else resolve(firstResult.data);
        });
    }

    const goFetchFoodList = (categoryData) => {
        if (isLoadingFoodItems) return;

        setLoadingFoodItems(true);
        getFoodItems(truckData.id, categoryData.id, 1)
            .then((result) => {
                getAllFoodData(result)
                    .then((categoryFoodList) => {
                        const newFoodData = cloneDeep(foodListData);
                        newFoodData[categoryData.id] = categoryFoodList.sort((a, b) => a.order - b.order);
                        setFoodListData(newFoodData);
                        setLoadingFoodItems(false);
                    });
            })
            .catch((error) => {
                const newFoodData = cloneDeep(foodListData);
                newFoodData[categoryData.id] = error;
                setFoodListData(newFoodData);
                setLoadingFoodItems(false);
            });
    }
    // endregion

    // region Function - Create New Category
    const createNewCategory = () => {
        showAlert({
            text: 'Enter new category name',
            showTextInput: true,
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Confirm',
                color: GLOBAL.COLOR.MAIN,
                onPress: (newName) => {
                    if (!newName?.trim()) return;

                    // Check if name already existed
                    const existedCategory = menuData.find((item) => (item.name.trim().toLowerCase() === newName.toLowerCase()));
                    if (existedCategory) {
                        showAlert({ text: `Your entered category name ("${newName}") is already existed in your food truck's menu, please select another name.` });
                        return;
                    }

                    // Execute name change
                    setCreatingCategoryName(newName?.trim());
                    createCategory(truckData.id, newName, menuData.length + 1)
                        .then((newCategory) => {
                            const newMenuData = menuData.concat(Object.assign({}, newCategory, { key: `categoryItem_${newCategory.id}`, isNew: true }));

                            // Save new menu data to state
                            setMenuData(newMenuData);

                            // Save to global state (mainly for ModifierCopyModal)
                            setGlobalState(STATE_KEYS.MENU_DATA, cloneDeep(newMenuData));

                            // Notify
                            toast(`Category "${newCategory.name}" has been successfully created`);
                        })
                        .catch((error) => showAppError(error))
                        .finally(() => setCreatingCategoryName(null));
                }
            }
        })
    };
    // endregion

    // region Function - Rename Category
    const renameCategory = () => {
        showAlert({
            text: `Enter new name for the category <b style="color: rgb(0, 117, 219); font-weight: bold">${selectedCategory.name}</b>`,
            showTextInput: true,
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Confirm',
                color: GLOBAL.COLOR.MAIN,
                onPress: (newName) => {
                    if (!newName?.trim()) return;

                    // Check if name already existed
                    const existedCategory = menuData.find((item) => (item.name.trim().toLowerCase() === newName.toLowerCase()) && (item.id !== selectedCategory.id));
                    if (existedCategory) {
                        showAlert({ text: `Your entered category name ("<b>${newName}</b>") is already existed in your food truck's menu, please select another name.` });
                        return;
                    }

                    // Execute name change
                    setRenamingCategory(true);
                    editCategory(truckData.id, selectedCategory.id, newName.trim())
                        .then((newCategoryData) => {
                            const categoryIndex = menuData.findIndex((item) => item.id === selectedCategory.id);
                            const tempArr = cloneDeep(menuData);
                            tempArr[categoryIndex] = Object.assign({ timestamp: moment().unix() }, tempArr[categoryIndex], newCategoryData);

                            // Save new menu data to state
                            setMenuData(tempArr);

                            // Save to global state (mainly for ModifierCopyModal)
                            setGlobalState(STATE_KEYS.MENU_DATA, cloneDeep(tempArr));

                            // Notify
                            toast(`Category "${selectedCategory.name}" has been changed to "${newName}"`);
                        })
                        .catch((error) => showAppError(error))
                        .finally(() => setRenamingCategory(false));
                }
            }
        })
    };
    // endregion

    // region Function - Delete Category
    const goDeleteCategory = () => {
        if (isDeletingCategory) return;

        showAlert({
            text: `Are you sure you want to delete this category along with all of its <b style="color: red">${selectedCategory['food_items_count'] + ' food item' + (selectedCategory['food_items_count'] > 1 ? 's' : '')}</b>? This action cannot be undone.<br/><br/><p style="color: rgb(0, 117, 219); font-weight: bold">${selectedCategory.name}</p>`,
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Confirm',
                color: GLOBAL.COLOR.MAIN,
                onPress: () => {
                    setDeletingCategory(true);
                    deleteCategory(truckData.id, selectedCategory.id)
                        .then(() => {
                            const newMenuData = cloneDeep(menuData);
                            const deletingIndex = menuData.findIndex((item) => item.id === selectedCategory.id);
                            newMenuData.splice(deletingIndex, 1);

                            // Move to new index
                            if (menuData.length === 1) {
                                setSelectedCategory(null);
                            }
                            else setSelectedCategory((deletingIndex === (menuData.length - 1)) ? menuData[deletingIndex - 1] : menuData[deletingIndex + 1]);

                            // Save to state
                            setMenuData(newMenuData);

                            // Save to global state (mainly for ModifierCopyModal)
                            setGlobalState(STATE_KEYS.MENU_DATA, cloneDeep(newMenuData));

                            // Notify
                            toast(`Category "${selectedCategory.name}" has been removed from your menu`);
                        })
                        .catch((error) => showAppError(error))
                        .finally(() => setDeletingCategory(false));
                }
            }
        })
    }
    // endregion

    // region Function - Create New Food Item
    const _onFoodItemPressed = (itemData = null) => {
        const currentFoodItemList = cloneDeep(foodListData[selectedCategory.id] || []);
        RootNavigation.navigate(routeKeys.MENU_ITEM_DETAILS, {
            itemData,
            categoryData: selectedCategory,
            currentFoodItemList,
            lastFoodItemPosition: currentFoodItemList[currentFoodItemList.length - 1]?.order || 0,
            onFoodItemUpdated: (updatedItem) => {
                const itemIndex = currentFoodItemList.findIndex((item) => item.id === updatedItem.id);
                if (itemIndex > -1) {
                    const newFoodListData = cloneDeep(foodListData);
                    currentFoodItemList.splice(itemIndex, 1, updatedItem);
                    newFoodListData[selectedCategory.id] = currentFoodItemList;
                    setFoodListData(newFoodListData);
                }
            },
            onNewFoodItemAdded: (newItem, categoryID) => {
                // Update food list data
                const newFoodListData = cloneDeep(foodListData);
                newFoodListData[selectedCategory.id] = currentFoodItemList.concat(newItem);
                setFoodListData(newFoodListData);

                // Update item count in category list
                const categoryIndex = menuData.findIndex((item) => item.id === categoryID);
                if (categoryIndex > -1) {
                    const newMenuData = cloneDeep(menuData);
                    newMenuData[categoryIndex]['food_items_count'] += 1;
                    setMenuData(newMenuData);

                    // Save to global state (mainly for ModifierCopyModal)
                    setGlobalState(STATE_KEYS.MENU_DATA, cloneDeep(newMenuData));
                }
            },
            onFoodItemRemoved: (removedID, categoryID) => {
                // Update food list data
                const newFoodListData = cloneDeep(foodListData);
                newFoodListData[selectedCategory.id] = currentFoodItemList.filter((item) => item.id !== removedID);
                setFoodListData(newFoodListData);

                // Update item count in category list
                const categoryIndex = menuData.findIndex((item) => item.id === categoryID);
                if (categoryIndex > -1) {
                    const newMenuData = cloneDeep(menuData);
                    newMenuData[categoryIndex]['food_items_count'] -= 1;
                    setMenuData(newMenuData);

                    // Save to global state (mainly for ModifierCopyModal)
                    setGlobalState(STATE_KEYS.MENU_DATA, cloneDeep(newMenuData));
                }
            }
        });
    }
    // endregion

    // region Function - Fetch Catering Menu Sample Files
    const getCateringSampleAlbumID = () => truckData.albums?.find((item) => item.name.toLowerCase() === 'catering')?.id;
    const goFetchCateringMenuSamples = () => {
        const cateringSampleAlbumID = getCateringSampleAlbumID();
        if (!cateringSampleAlbumID) return;

        setLoadingCateringSample(true);
        getCateringMenuSampleFiles(truckData.id, cateringSampleAlbumID)
            .then((result) => setSampleMenuFiles(result.data))
            .catch((error) => showAppError(error))
            .finally(() => setLoadingCateringSample(false));
    }
    // endregion

    // region Function - Go Upload Catering Sample
    const goUploadCateringSample = (file) => {
        const maxFileSize = 10 * 1024 * 1024;
        const cateringSampleAlbumID = getCateringSampleAlbumID();
        if (!cateringSampleAlbumID) return;

        // Block if file is too large
        if (file.size > maxFileSize) {
            showAlert({ text: 'The file you selected exceeded our maximum file size limit of 10 MB, please select another one.' });
            return;
        }

        // Start uploading
        setUploadingSampleMenuFile(true);
        uploadCateringMenuSampleFile(truckData.id, cateringSampleAlbumID, file)
            .then((newFileList) => setSampleMenuFiles(newFileList))
            .catch((uploadErr) => showAppError(uploadErr))
            .finally(() => setUploadingSampleMenuFile(false));
    }
    // endregion

    // region Function - Delete Catering Sample File
    const _onDeleteFilePressed = (item) => {
        showAlert({
            text: `Are you sure you want to delete this file?<br/><br/><b style="color: rgb(0, 117, 219); font-weight: bold">${item.name}</b><br/><br/>This action cannot be undone`,
            confirmButton: {
                text: 'Confirm',
                color: 'red',
                onPress: () => {
                    const cateringSampleAlbumID = getCateringSampleAlbumID();
                    if (!cateringSampleAlbumID) return;

                    setDeletingFileID(item.id);
                    deleteCateringMenuSampleFile(truckData.id, cateringSampleAlbumID, item.id)
                        .then(() => setSampleMenuFiles(sampleMenuFiles.filter((file) => file.id !== item.id)))
                        .catch((error) => showAppError(error))
                        .finally(() => setDeletingFileID(null));
                }
            },
            cancelButton: { text: 'Cancel' }
        });
    };
    // endregion

    // region Render Import Menu From Square Section
    const renderImportMenuFromSquare = () => (
        !isProfilePendingApproval() ?
            (
                (!isLoading && (menuData.length === 0) && !!GLOBAL.DATA.USER['squareup_token']) ?
                    <TouchableOpacity
                        onPress={() => showAlert({ text: 'This feature is not available yet on the web, please use the Truckster Vendor app to import your menu from Square' })}
                        style={{ width: '100%', height: 72, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: GLOBAL.COLOR.BASIC.BLACK_TWO, paddingHorizontal: 24 }}>
                        <Image
                            source={require('../../images/image_square_big.png')}
                            style={{ width: 40, height: 40, tintColor: 'white' }}
                            resizeMode={'contain'}
                        />

                        <Text style={{ fontWeight: 'bold', fontSize: 18, marginLeft: 8, color: 'white' }}>
                            Import menu from your Square account
                        </Text>
                    </TouchableOpacity>
                    :
                    null
            )
            :
            null
    )
    // endregion

    // region Render New Category Button
    const renderNewCategoryButton = (isMenuEmpty = false) => {
        let delayCount = isMenuEmpty ? 0 : ((menuData?.length || 0) + 1),
            iconSize = isMenuEmpty ? 32 : 24,
            iconContainerSize = 56;

        const isCreatingCategory = !!creatingCategoryName?.trim();

        return(
            <TouchableOpacity
                onPress={() => createNewCategory()}
                style={{ alignSelf: isMenuEmpty ? 'center' : 'stretch', paddingHorizontal: 24, paddingVertical: 18 }}>
                <Animatable.View
                    animation={'fadeIn'} delay={delayCount * DELAY_VALUE}
                    style={{ flexDirection: 'row', alignItems: 'center', alignSelf: 'stretch' }}>
                    <View style={{ width: iconContainerSize, height: iconContainerSize, borderRadius: iconContainerSize / 2, alignItems: 'center', justifyContent: 'center', backgroundColor: GLOBAL.COLOR.BASIC.UI_BLUE }}>
                        {
                            isCreatingCategory ?
                                <ActivityIndicator
                                    color={'white'}
                                    style={{ width: iconSize, height: iconSize }}
                                />
                                :
                                <Image
                                    source={require('../../images/iconLinePlus@3x.png')}
                                    style={{ width: iconSize, height: iconSize, tintColor: 'white' }}
                                />
                        }
                    </View>

                    {
                        !isMenuEmpty &&
                        <Text style={{ fontWeight: '500', color: GLOBAL.COLOR.BASIC.UI_BLUE, fontSize: 18, marginLeft: 16 }}>
                            {isCreatingCategory ? 'Creating Category...' : 'New Category'}
                        </Text>
                    }
                </Animatable.View>
            </TouchableOpacity>
        );
    }
    // endregion

    // region Render Empty View
    const renderEmptyView = () => (
        <View style={{ flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, textAlign: 'center', marginTop: 16 }}>
                {
                    creatingCategoryName?.trim() ?
                        `Creating "${creatingCategoryName}"...`
                        :
                        'Tap to create your first menu category'
                }
            </Text>

            {renderNewCategoryButton(true, !!creatingCategoryName?.trim())}
        </View>
    )
    // endregion

    // region Render Category Item
    const renderCategoryItem = (categoryData, index) => {
        const isChosen = selectedCategory?.id === categoryData.id;
        const foodDataLoaded = foodListData.hasOwnProperty(categoryData.id) && (typeof foodListData[categoryData.id] !== 'string');
        return(
            <Animatable.View
                key={`categoryItem_${categoryData.id}`}
                animation={categoryData.isNew ? 'fadeInRight' : 'fadeInUp'} duration={600} delay={categoryData.isNew ? 0 : (index * DELAY_VALUE)}
                style={{ width: '100%', borderBottomWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER }}>
                <TouchableOpacity
                    onPress={() => {
                        if (!isChosen) {
                            setSelectedCategory(categoryData);
                            (!foodDataLoaded) && goFetchFoodList(categoryData);
                        }
                    }}
                    style={{ width: '100%', justifyContent: 'center', alignItems: 'flex-start', paddingLeft: 16, paddingVertical: 16, backgroundColor: isChosen ? GLOBAL.COLOR.MAIN_SELECTED : 'white' }}>
                    <View style={{ flex: 1, paddingHorizontal: 8, flexDirection: 'row', alignItems: 'center' }}>
                        {/* Order */}
                        <View style={{ width: 56, height: 56, borderRadius: 28, backgroundColor: 'rgb(245, 196, 178)', alignItems: 'center', justifyContent: 'center', marginRight: 16 }}>
                            <Image
                                source={require('../../images/iconLineFoodItem@3x.png')}
                                style={{ width: 24, height: 24 }}
                            />
                        </View>

                        {/* Content View */}
                        <View style={{ flex: 1, alignItems: 'flex-start', justifyContent: 'center' }}>
                            {/* Name */}
                            <Text style={{ color: 'black', fontSize: 18, fontWeight: '500', letterSpacing: -0.1, textAlign: 'left' }} numberOfLines={2}>
                                {toTitleCase(categoryData.name)}
                            </Text>

                            {/* Food Item Count */}
                            <Text style={{ color: GLOBAL.COLOR.TEXT_LIGHT_GRAY, fontSize: 16, fontWeight: 'normal', letterSpacing: -0.1, marginTop: 4 }}>
                                {categoryData.food_items_count + ' item' + (categoryData.food_items_count > 1 ? 's' : '')}
                            </Text>
                        </View>
                    </View>
                </TouchableOpacity>
            </Animatable.View>
        );
    }
    // endregion

    // region Render Food Item
    const renderFoodItem = (foodData, index) => {
        const imageSize = 100;
        const hasSquare = squareLocations?.length > 0;
        const hasMultipleSquare = squareLocations?.length > 1;

        let inactiveCount = 0;
        (hasMultipleSquare) && (foodData['menu_status'] || []).forEach((item) => Number(item.status) === 0 && (inactiveCount += 1));

        return(
            <Animatable.View
                key={`categoryItem_${foodData.id}`}
                animation={'fadeInUp'} duration={600} delay={index * DELAY_VALUE}
                style={{ alignSelf: 'stretch', borderRadius: 8, borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, marginTop: 16, marginHorizontal: 16, overflow: 'hidden' }}>
                <TouchableOpacity
                    onPress={() => _onFoodItemPressed(foodData)}
                    style={{ width: '100%', flexDirection: 'row', justifyContent: 'center', alignItems: 'flex-start', paddingLeft: 16, paddingVertical: 16, backgroundColor: 'white' }}>
                    {/* Photo */}
                    <Image
                        source={foodData.photo ? { uri: foodData.photo } : require('../../images/illustration-empty-food.png')}
                        style={{ width: imageSize, height: imageSize }}
                        resizeMode={'cover'}
                    />

                    {/* Content View */}
                    <View style={{ alignSelf: 'stretch', flex: 1, marginLeft: 16 }}>
                        {/* Name */}
                        <Text style={{ color: 'black', fontSize: 18, fontWeight: '500', alignSelf: 'stretch' }} numberOfLines={1}>
                            {foodData.name}
                        </Text>

                        {/* Description */}
                        <View style={{ flex: 1, marginRight: 16 }}>
                            {
                                (hasMultipleSquare && inactiveCount > 0) ?
                                    false
                                    :
                                    <Text style={{ color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY, fontSize: 16, fontWeight: 'normal', marginTop: 8, alignSelf: 'stretch' }} numberOfLines={1}>
                                        {foodData.description || '-'}
                                    </Text>
                            }
                        </View>

                        {/* Price */}
                        <Text style={{ color: 'black', fontSize: 18 }}>
                            ${Number(foodData.price).toFixed(2)}
                        </Text>

                        {/* Item Status (Multi) */}
                        {
                            (hasMultipleSquare && inactiveCount > 0) &&
                            <Text style={{ fontWeight: '450', fontSize: 12, color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY, textAlign: 'center', marginTop: 8 }}>
                                ({'Inactive in ' + (inactiveCount === foodData['menu_status'].length ? 'all' : inactiveCount) + ' location' + (inactiveCount > 1 ? 's' : '')})
                            </Text>
                        }
                    </View>

                    {/* Item Status (Single) */}
                    {
                        (!hasMultipleSquare && ((hasSquare && Number(foodData['menu_status']?.[0]?.status || 0) !== 1) || (!hasSquare && foodData['status'] !== 1))) &&
                        <View
                            style={{
                                position: 'absolute', bottom: 16, right: 4,
                                borderColor: GLOBAL.COLOR.TEXT_GRAY,
                                borderRadius: 5, paddingVertical: 4, paddingHorizontal: 8, marginLeft: 10, borderWidth: 1
                            }}>
                            <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, alignSelf: 'center', backgroundColor: 'transparent', fontSize: 10 }}>
                                INACTIVE
                            </Text>
                        </View>
                    }
                </TouchableOpacity>
            </Animatable.View>
        )
    };
    // endregion

    // region Render New Food Item Button
    const renderNewFoodItemButton = () => (
        <TouchableOpacity onPress={() => _onFoodItemPressed()}>
            <View
                style={{
                    width: 48, height: 48, borderRadius: 48 / 2, marginTop: 24,
                    alignItems: 'center', justifyContent: 'center', backgroundColor: GLOBAL.COLOR.BASIC.UI_BLUE
                }}>
                <Image
                    source={require('../../images/iconLinePlus@3x.png')}
                    style={{ width: 24, height: 24, tintColor: 'white' }}
                />
            </View>
        </TouchableOpacity>
    )
    // endregion

    // region Render Toolbar
    const TOOLBAR_HEIGHT = 64;
    const renderToolbarItem = (icon, text, onPress = () => {}, isFirst = false) => (
        <TouchableOpacity
            onPress={onPress}
            style={{ height: TOOLBAR_HEIGHT, marginHorizontal: 24, flexDirection: 'row', alignItems: 'center' }}>
            <FeatherIcon name={icon} size={24} color={'white'} />
            <Text style={{ color: 'white', fontSize: 16, fontWeight: '500', marginLeft: 8 }}>
                {text}
            </Text>
        </TouchableOpacity>
    );

    const renderCategoryToolbar = (categoryData) => (
        <Animatable.View
            animation={'slideInUp'}
            delay={500}
            style={{
                position: 'absolute', bottom: 0, backgroundColor: 'black', borderTopLeftRadius: 8, borderTopRightRadius: 8,
                alignSelf: 'center', boxShadow: '0px 1px 5px #aeafb1, 0px -1px 5px #aeafb1',
                flexDirection: 'row', alignItems: 'center', justifyContent: 'center'
            }}>
            {renderToolbarItem('plus-circle', 'New Item', () => _onFoodItemPressed(), true)}
            <View style={{ width: 1, height: 24, backgroundColor: 'white' }} />
            {renderToolbarItem('edit-3', 'Rename Category', () => renameCategory())}
            <View style={{ width: 1, height: 24, backgroundColor: 'white' }} />
            {renderToolbarItem('trash', 'Delete Category', () => goDeleteCategory())}
        </Animatable.View>
    )
    // endregion

    // region Render Toolbar
    const renderTabButton = (text = '', isActive = false, onPress = () => {}) => (
        <TouchableOpacity
            onPress={onPress}
            style={[
                {
                    height: 48, flex: 1, alignItems: 'center', justifyContent: 'center',
                    backgroundColor: isActive ? 'black' : 'white', borderColor: isActive ? 'black' : GLOBAL.COLOR.DIVIDER,
                    borderLeftWidth: 1, borderRightWidth: 1
                }
            ]}>
            <Text style={{ color: isActive ? 'white' : GLOBAL.COLOR.BASIC.BLACK_TWO, fontSize: 16, fontWeight: isActive ? 'bold' : 'normal' }}>
                {text}
            </Text>
        </TouchableOpacity>
    );

    const renderToolbar = () => {
        return(
            <View style={{ width: '100%', paddingVertical: 8, backgroundColor: 'white', flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: inScreenModalShowing ? 'rgba(0, 0, 0, 0.8)' : GLOBAL.COLOR.DIVIDER }}>
                {/* Back Button */}
                <TouchableOpacity
                    onPress={() => RootNavigation.goBack()}
                    style={{ width: TOOLBAR_HEIGHT, height: TOOLBAR_HEIGHT, alignItems: 'center', justifyContent: 'center' }}>
                    <FeatherIcon name={'arrow-left'} size={32} color={GLOBAL.COLOR.BASIC.BLACK_TWO} />
                </TouchableOpacity>

                {/* Segment Control */}
                <View style={{ flex: 1 }}>
                    <View style={{
                        width: '50%', backgroundColor: GLOBAL.COLOR.BASIC.GRAY_OPAQUE, flexDirection: 'row', alignSelf: 'center',
                        borderColor: GLOBAL.COLOR.DIVIDER, borderRadius: 8, borderWidth: 1, overflow: 'hidden'
                    }}>
                        {renderTabButton('Truck Menu', tabIndex === 0, () => {
                            setTabIndex(0);
                            menuView.current?.fadeZoom(600);
                        })}
                        {renderTabButton('Catering Menu', tabIndex === 1, () => {
                            setTabIndex(1);
                            cateringSampleView.current?.fadeZoom(600);
                        })}
                        {renderTabButton('Saved Proposals', tabIndex === 2, () => {
                            setTabIndex(2);
                            proposalView.current?.fadeZoom(600);
                        })}

                        {
                            isLoading &&
                            <View style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.7)' }}/>
                        }
                    </View>
                </View>

                {/* Blank Space */}
                <View style={{ width: TOOLBAR_HEIGHT, height: TOOLBAR_HEIGHT }} />

                {/* In-Screen Modal Backdrop */}
                <Animatable.View
                    pointerEvents={inScreenModalShowing ? 'auto' : 'none'}
                    animation={inScreenModalShowing ? 'fadeIn' : 'fadeOut'}
                    duration={400}
                    style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
                />
            </View>
        )
    }
    // endregion

    // region Render Menu View
    const renderMenuView = () => (
        <Animatable.View ref={menuView} style={{ width: '100%', flex: 1, display: tabIndex === 0 ? 'flex' : 'none' }}>
            {/* Import Menu from Square */}
            {renderImportMenuFromSquare()}

            {/* Content */}
            {
                menuData.length > 0 ?
                    <View style={{ width: '100%', flex: 1, flexDirection: 'row' }}>
                        {/* Category */}
                        <View style={{ width: '30%', minWidth: 350, maxWidth: 500, height: '100%' }}>
                            <ScrollView
                                style={{ flex: 1, width: '100%', backgroundColor: 'white' }}
                                showsVerticalScrollIndicator={false}>
                                {/* List of Categories */}
                                {menuData.map((item, index) => renderCategoryItem(item, index))}

                                {/* Create New Category Button */}
                                {renderNewCategoryButton(false)}
                            </ScrollView>
                        </View>

                        {/* Items */}
                        <View style={{ flex: 1 }}>
                            {
                                isLoadingFoodItems ?
                                    false
                                    :
                                    (
                                        selectedCategory ?
                                            (
                                                typeof foodListData[selectedCategory.id] !== 'string' ?
                                                    (
                                                        foodListData[selectedCategory.id]?.length > 0 ?
                                                            <ScrollView
                                                                style={{ width: '100%', flex: 1, borderLeftWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER }}
                                                                contentContainerStyle={{ paddingBottom: 128 }}>
                                                                {foodListData[selectedCategory.id].map((item, index) => renderFoodItem(item, index))}
                                                            </ScrollView>
                                                            :
                                                            <Animatable.View
                                                                animation={'fadeIn'}
                                                                style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                                                                <Text style={{ color: GLOBAL.COLOR.BASIC.BLACK_TWO, textAlign: 'center', marginHorizontal: 48, fontSize: 20, fontWeight: 'bold' }}>
                                                                    You have no food items in this category
                                                                </Text>

                                                                <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, textAlign: 'center', marginTop: 16 }}>
                                                                    Tap to create one
                                                                </Text>

                                                                {/* Add Food Item Button */}
                                                                {renderNewFoodItemButton()}
                                                            </Animatable.View>
                                                    )
                                                    :
                                                    <Animatable.View animation={'fadeIn'} style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                                                        <Text style={{ color: GLOBAL.COLOR.TEXT_LIGHT_GRAY, textAlign: 'center', fontSize: 20 }}>
                                                            Failed to load food items for this category
                                                            {'\n\n'}
                                                            Error Message: {foodListData[selectedCategory.id]}
                                                        </Text>
                                                    </Animatable.View>
                                            )
                                            :
                                            <Animatable.View animation={'fadeIn'} style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                                                <Text style={{ color: GLOBAL.COLOR.TEXT_LIGHT_GRAY, textAlign: 'center', fontSize: 20 }}>
                                                    Select a category on the left to see its items
                                                </Text>
                                            </Animatable.View>
                                    )
                            }

                            {/* Category Toolbar */}
                            {!!selectedCategory && renderCategoryToolbar()}
                        </View>
                    </View>
                    :
                    renderEmptyView()
            }
        </Animatable.View>
    )
    // endregion

    // region Render Catering Menu View
    const renderCateringMenuView = () => (
        <Animatable.View ref={cateringSampleView} style={{ flex: 1, width: '100%', display: tabIndex === 1 ? 'flex' : 'none' }}>
            {
                isLoadingCateringSample ?
                    <LoadingView description={'Loading catering menu sample'} />
                    :
                    <ScrollView
                        style={{ flex: 1, width: '100%' }}
                        contentContainerStyle={{ paddingTop: 48, width: '50%', alignSelf: 'center' }}>
                        {/* Title */}
                        <Text style={{ fontSize: 32, fontWeight: '900', color: 'black' }}>
                            Catering Menu Sample
                        </Text>

                        {/* Description */}
                        <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, fontSize: 18, lineHeight: 28, fontWeight: 'normal', marginVertical: 16, alignSelf: 'stretch' }}>
                            Upload samples of your menu for catering service here. These will be shared with booking clients when your truck is recommended.
                        </Text>

                        {/* File List */}
                        {
                            sampleMenuFiles?.map((item, index) => (
                                <View
                                    key={item.id}
                                    pointerEvents={uploadingSampleMenuFile ? 'none' : 'auto'}
                                    style={{
                                        alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center',
                                        paddingVertical: 24, paddingHorizontal: 24, opacity: uploadingSampleMenuFile ? 0.5 : 1,
                                        borderRadius: 8, borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER,
                                        backgroundColor: 'white', marginTop: 12
                                    }}>
                                    {/* Open File Button */}
                                    <TouchableOpacity
                                        onPress={() => window.open(item.path, '_blank')}
                                        style={{ flex: 1, flexDirection: 'row', alignItems: 'center', marginRight: 24 }}>
                                        {/* Icon */}
                                        <FeatherIcon
                                            name={getUrlExtension(item.path) === 'pdf' ? 'file-text' : 'image'}
                                            color={GLOBAL.COLOR.MAIN}
                                            size={24}
                                        />

                                        {/* File Name */}
                                        <Text style={{ marginLeft: 12, flex: 1 }} numberOfLines={1}>
                                            {item.name ? getFileName(item.name) : `(File #${index + 1})`}
                                        </Text>
                                    </TouchableOpacity>

                                    {/* Delete Button */}
                                    <TouchableOpacity onPress={() => _onDeleteFilePressed(item)}>
                                        {
                                            deletingFileID ?
                                                (
                                                    (deletingFileID === item.id) ?
                                                        <ActivityIndicator color={GLOBAL.COLOR.BASIC.GRAY}/>
                                                        :
                                                        false
                                                )
                                                :
                                                <FeatherIcon name={'trash'} color={GLOBAL.COLOR.BASIC.GRAY} size={24}/>
                                        }
                                    </TouchableOpacity>
                                </View>
                            ))
                        }

                        {/* File Picker */}
                        <input
                            id={'cateringSamplePicker'}
                            type={'file'}
                            accept={'.pdf, .png, .jpg, .jpeg'}
                            ref={cateringSamplePicker}
                            style={{ display: 'none' }}
                            onChange={(event) => {
                                event.stopPropagation();
                                event.preventDefault();
                                const file = event.target.files[0];
                                goUploadCateringSample(file);
                            }}
                        />

                        {/* Cover Picker Button */}
                        <Button
                            text={'Add New File'}
                            onPress={() => cateringSamplePicker.current?.click()}
                            style={{ backgroundColor: GLOBAL.COLOR.MAIN, marginTop: 12 }}
                            textColor={'white'}
                            image={'plus'}
                            useFeather={true}
                            imageSize={28}
                            imageNextToText={true}
                            locked={uploadingSampleMenuFile}
                        />
                    </ScrollView>
            }
        </Animatable.View>
    )
    // endregion

    // region Render Proposal View
    const renderProposalView = () => (
        <Animatable.View
            ref={proposalView}
            style={{ width: '100%', flex: 1, alignItems: 'center', justifyContent: 'center', display: tabIndex === 2 ? 'flex' : 'none' }}>
            <ProposalTab />
        </Animatable.View>
    )
    // endregion

    // region Render Content
    const renderContent = () => (
        <View style={{ flex: 1, width: '100%' }}>
            {renderMenuView()}
            {renderCateringMenuView()}
            {renderProposalView()}
        </View>
    )
    // endregion

    // region MAIN RENDER
    return(
        <View style={{ flex: 1, width: '100%' }}>
            {/* Nav Bar */}
            {renderToolbar()}

            {/* Content */}
            {
                isLoading ?
                    <LoadingView isLoading={true} />
                    :
                    (
                        errorMessage?.trim() ?
                            <View style={{ flex: 1, alignSelf: 'stretch', alignItems: 'center', justifyContent: 'center', padding: 64 }}>
                                <Text style={{ color: GLOBAL.COLOR.TEXT_LIGHT_GRAY, textAlign: 'center' }}>
                                    Unable to fetch menu data from Truckster server, please try again later
                                    {'\n\n'}
                                    Error message: {errorMessage}
                                </Text>
                            </View>
                            :
                            renderContent()
                    )
            }

            {/* Loading View for Food Items */}
            {
                (isLoadingFoodItems || isDeletingCategory || isRenamingCategory || uploadingSampleMenuFile) &&
                <LoadingView isLoading={true} isAbsolute={true} description={uploadingSampleMenuFile ? 'Uploading catering menu sample file' : ''} />
            }
        </View>
    );
    // endregion
}
